import type * as React from 'react';
import { Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

interface SearchInputProps {
	value: string;
	onValueChange: (value: string) => void;
	placeholder?: string;
	isLoading?: boolean;
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
	renderResults: () => React.ReactNode;
	renderHeader?: () => React.ReactNode;
	emptyMessage?: string;
	className?: string;
	popoverClassName?: string;
	contentClassName?: string;
	disabled?: boolean;
	totalResults?: number;
}

export function SearchInput({
	value,
	onValueChange,
	placeholder = 'Search...',
	isLoading = false,
	isOpen,
	onOpenChange,
	renderResults,
	renderHeader,
	emptyMessage = 'No results found.',
	className,
	popoverClassName,
	contentClassName,
	disabled = false,
	totalResults = 0
}: SearchInputProps) {
	const renderContent = () => {
		if (isLoading) {
			return <div className="p-4 text-center text-sm text-slate-500">Searching...</div>;
		}

		if (!value.trim()) {
			return <div className="p-4 text-center text-sm text-slate-500">Start typing to search...</div>;
		}

		if (totalResults === 0 && !isLoading) {
			return <div className="p-4 text-center text-sm text-slate-500">{emptyMessage}</div>;
		}

		return renderResults();
	};

	return (
		<Popover open={isOpen} onOpenChange={onOpenChange}>
			<PopoverTrigger asChild>
				<div className="relative" onClick={e => e.stopPropagation()}>
					<Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
					<Input
						placeholder={placeholder}
						value={value}
						onChange={e => onValueChange(e.target.value)}
						onFocus={() => onOpenChange(true)}
						onClick={e => {
							e.stopPropagation();
							onOpenChange(true);
						}}
						className={cn('pl-10', className)}
						disabled={disabled}
					/>
					{totalResults > 0 && (
						<Badge
							variant="secondary"
							className="absolute right-2 top-1/2 transform -translate-y-1/2 text-xs"
						>
							{totalResults}
						</Badge>
					)}
				</div>
			</PopoverTrigger>

			<PopoverContent
				className={cn('p-0', popoverClassName)}
				align="start"
				onOpenAutoFocus={e => e.preventDefault()}
			>
				{renderHeader && renderHeader()}
				<ScrollArea className={cn('h-[400px]', contentClassName)}>{renderContent()}</ScrollArea>
			</PopoverContent>
		</Popover>
	);
}
